import { z } from "zod";
import { EmailDeliverySchema } from "./emailDelivery";
import { LineItemSchema } from "./sales";

// Purchase orders to suppliers. A PO is the company's order document: lines
// (pricebook items or free text, each optionally for a job and tied to that
// job's part on order), emailed to the supplier as a PDF. The supplier's order
// acknowledgment (uploaded, read by AI) confirms quantities, prices and the
// ship date; receiving puts parts into stock and marks the jobs' parts arrived.
// Written by functions only.

export const PO_STATUSES = ["draft", "sent", "acknowledged", "partial", "received", "canceled"] as const;
export const PurchaseOrderStatusSchema = z.enum(PO_STATUSES);
export type PurchaseOrderStatus = z.infer<typeof PurchaseOrderStatusSchema>;
export const PO_STATUS_LABEL: Record<PurchaseOrderStatus, string> = {
  draft: "Draft",
  sent: "Sent",
  acknowledged: "Confirmed",
  partial: "Partly received",
  received: "Received",
  canceled: "Canceled",
};

export const PurchaseOrderLineSchema = z.object({
  /** Stable within the PO (receiving and acknowledgments point at it). */
  key: z.string().min(1).max(40),
  description: z.string().trim().min(1).max(300),
  sku: z.string().max(80).default(""),
  /** Pricebook item: receiving adds it to stock. */
  itemId: z.string().nullable().default(null),
  quantity: z.number().positive().max(100000),
  unitCostCents: z.number().int().min(0).max(100000000).nullable().default(null),
  receivedQty: z.number().min(0).max(100000).default(0),
  /** The job this line is for (and its part on order, which tracks with it). */
  jobId: z.string().nullable().default(null),
  jobTitle: z.string().max(200).default(""),
  orderedItemId: z.string().nullable().default(null),
  /** What the supplier's acknowledgment said, when it differed. */
  ackNote: z.string().max(300).default(""),
});
export type PurchaseOrderLine = z.infer<typeof PurchaseOrderLineSchema>;

export const PurchaseOrderSchema = z.object({
  number: z.number().int().positive(),
  status: PurchaseOrderStatusSchema.default("draft"),
  vendorId: z.string().nullable().default(null),
  vendorName: z.string().max(160).default(""),
  vendorEmail: z.string().max(200).default(""),
  lines: z.array(PurchaseOrderLineSchema).min(1).max(200),
  /** Where it ships: the yard, or a job site. */
  shipTo: z.string().max(500).default(""),
  notes: z.string().max(5000).default(""),
  /** When the parts should arrive (the acknowledgment's date wins). */
  expectedAt: z.number().nullable().default(null),
  subtotalCents: z.number().int().min(0).default(0),
  sentAt: z.number().nullable().default(null),
  receivedAt: z.number().nullable().default(null),
  acknowledgment: z
    .object({
      at: z.number(),
      byName: z.string(),
      /** The supplier's own order / confirmation number. */
      vendorRef: z.string().max(80).default(""),
      expectedAt: z.number().nullable().default(null),
      fileName: z.string().max(200).default(""),
      storagePath: z.string().max(500).default(""),
      differences: z.array(z.string().max(300)).max(50).default([]),
    })
    .nullable()
    .default(null),
  emails: z
    .array(z.object({ at: z.number(), byName: z.string(), to: z.array(z.string()), ok: z.boolean(), error: z.string().default(""), id: z.string().nullable().optional() }))
    .default([]),
  /** Delivery news per email id. */
  emailDelivery: z.record(z.string(), EmailDeliverySchema).optional(),
  createdByUid: z.string(),
  createdByName: z.string().default(""),
  createdAt: z.number(),
  updatedAt: z.number(),
});
export type PurchaseOrder = z.infer<typeof PurchaseOrderSchema>;

export const poSubtotalCents = (lines: Array<{ quantity: number; unitCostCents: number | null }>) =>
  lines.reduce((s, l) => s + Math.round(l.quantity * (l.unitCostCents ?? 0)), 0);

// ---- Maintenance agreements ------------------------------------------------
// A customer's recurring plan: what's billed each period (lines), how often,
// the department it belongs to and its terms, and how many maintenance visits
// a year it includes. A daily run bills the due ones (a draft for review, or
// sent by email) and opens a job for each visit as it comes due.

export const AGREEMENT_FREQUENCIES = ["monthly", "quarterly", "semiannual", "annual"] as const;
export const AgreementFrequencySchema = z.enum(AGREEMENT_FREQUENCIES);
export type AgreementFrequency = z.infer<typeof AgreementFrequencySchema>;
export const FREQUENCY_MONTHS: Record<AgreementFrequency, number> = { monthly: 1, quarterly: 3, semiannual: 6, annual: 12 };
export const FREQUENCY_LABEL: Record<AgreementFrequency, string> = { monthly: "Monthly", quarterly: "Every 3 months", semiannual: "Every 6 months", annual: "Yearly" };

export const ServiceAgreementSchema = z.object({
  customerId: z.string(),
  customerName: z.string().default(""),
  name: z.string().trim().min(1).max(120),
  departmentId: z.string().nullable().default(null),
  terms: z.string().max(20000).default(""),
  lines: z.array(LineItemSchema).min(1).max(50),
  taxRateBps: z.number().int().min(0).max(5000).default(0),
  /** Per billing period, computed from the lines. */
  totalCents: z.number().int().min(0).default(0),
  frequency: AgreementFrequencySchema,
  /** "draft": each period's invoice waits for review; "send": it's emailed to the customer. */
  billing: z.enum(["draft", "send"]).default("draft"),
  /** Who gets the invoice email (defaults to the customer's email). */
  billingEmail: z.string().max(200).default(""),
  startAt: z.number(),
  /** Null = until canceled. */
  endAt: z.number().nullable().default(null),
  nextBillAt: z.number().nullable(),
  lastBilledAt: z.number().nullable().default(null),
  /** Maintenance visits a year (0 = billing only). */
  visitsPerYear: z.number().int().min(0).max(12).default(0),
  nextVisitAt: z.number().nullable().default(null),
  visitJobType: z.string().max(40).nullable().default(null),
  /** Member discount on the customer's estimates and invoices while the plan is active (basis points; 1500 = 15%). */
  discountBps: z.number().int().min(0).max(10000).default(0),
  status: z.enum(["active", "paused", "canceled"]).default("active"),
  salespersonUid: z.string().nullable().default(null),
  salespersonName: z.string().default(""),
  /** The last few invoices and visit jobs it made (newest last). */
  history: z
    .array(z.object({ at: z.number(), kind: z.enum(["invoice", "visit"]), refId: z.string(), label: z.string() }))
    .max(60)
    .default([]),
  lastError: z.string().default(""),
  createdByUid: z.string(),
  createdAt: z.number(),
  updatedAt: z.number(),
});
export type ServiceAgreement = z.infer<typeof ServiceAgreementSchema>;

/** The same day N months later (the 31st becomes the month's last day). */
export function addMonths(ms: number, months: number): number {
  const d = new Date(ms);
  const day = d.getUTCDate();
  const target = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + months, 1, d.getUTCHours(), d.getUTCMinutes()));
  const last = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)).getUTCDate();
  target.setUTCDate(Math.min(day, last));
  return target.getTime();
}
